import { PATTERN_DIAMETERS, patternHue, type PatternWeight } from '@nsb/core';

/**
 * A pattern's circle on the map (spec "Pattern Mapping" §4.3; Jason, 2026-10-17: "it should grow bigger in color and
 * boldness"): each of the five steps larger, its fill deeper in its scale's hue, its edge thicker and its name heavier;
 * at the top the full tone with the name in white. Its name sits in it, the circle never smaller than the name needs.
 * The colours are the style guide's tokens, mixed into the sheet, never new ones.
 */

const HUE = { slate: 'var(--color-slate)', moss: 'var(--color-moss)', cedar: 'var(--color-cedar-deep)' } as const;
const DEEP = { slate: 'color-mix(in srgb, var(--color-slate) 80%, var(--color-ink))', moss: 'var(--color-moss-deep)', cedar: 'color-mix(in srgb, var(--color-cedar-deep) 80%, var(--color-ink))' } as const;
/** How much of the hue is in each step's fill. */
const FILL = { 1: 8, 2: 14, 3: 28, 4: 52, 5: 100 } as const;
const EDGE = { 1: 1, 2: 1.25, 3: 1.5, 4: 2, 5: 2.5 } as const;
const FONT = { 1: 12, 2: 12.5, 3: 13, 4: 14, 5: 15 } as const;
const WEIGHT = { 1: 400, 2: 400, 3: 500, 4: 600, 5: 600 } as const;
/** The most lines a name takes in its circle. */
const MOST_LINES = 3;

export interface CircleLook {
  /** Its diameter, in the map's units. */
  d: number;
  fill: string;
  edge: string;
  border: number;
  fontSize: number;
  fontWeight: number;
  ink: string;
  /** Its name, as it is broken into lines in the circle. */
  lines: string[];
}

/** How wide a run of letters is at a size, near enough for Inter. */
const textWidth = (s: string, size: number) => s.length * size * 0.56;

/** A name broken into lines no wider than `width`, a word never broken; null when it takes more lines than a circle holds. */
export function nameLines(name: string, size: number, width: number): string[] | null {
  const lines: string[] = [];
  for (const word of name.split(/\s+/)) {
    if (textWidth(word, size) > width) return null;
    const last = lines.at(-1);
    if (last !== undefined && textWidth(`${last} ${word}`, size) <= width) lines[lines.length - 1] = `${last} ${word}`;
    else lines.push(word);
  }
  return lines.length <= MOST_LINES ? lines : null;
}

/** The room a circle of diameter `d` gives its name: the width across it where the lines sit, less a margin. */
const roomIn = (d: number) => d * 0.78 - 8;

/** The width across a circle of diameter `d`, `y` down it. */
const across = (d: number, y: number) => 2 * Math.sqrt(Math.max(0, (d / 2) ** 2 - (y - d / 2) ** 2));

/**
 * A painted circle, as shares of its diameter: its picture starts a little below its top, a gap clear of its name, never
 * less than this share of the circle nor more than it needs.
 */
const PICTURE_TOP = 0.08;
const PICTURE_GAP = 0.03;
const PICTURE_LEAST = 0.3;
const PICTURE_MOST = 0.56;
/** Kept clear each side of a line of a painted circle's name, inside its ring. */
const NAME_CLEAR = 4;

/**
 * Where a painted circle's picture and name sit, from the top inside its ring (Jason, 2026-10-22: "maybe don't have the
 * titles overlap the images"): the name as low as each of its lines still fits across the circle, and the picture in the
 * space above it, never under the name; `room`, the space the picture has.
 */
function paintedIn(d: number, lines: readonly string[], fontSize: number, ring: number) {
  const inner = d - 2 * ring;
  const lineH = fontSize * 1.15;
  const fits = (top: number) =>
    lines.every((l, i) => {
      const far = Math.max(Math.abs(top + i * lineH - inner / 2), Math.abs(top + (i + 1) * lineH - inner / 2));
      return textWidth(l, fontSize) + 2 * NAME_CLEAR <= across(inner, inner / 2 + far);
    });
  let nameTop = inner - lines.length * lineH;
  while (nameTop > 0 && !fits(nameTop)) nameTop -= 1;
  const room = nameTop - (PICTURE_TOP + PICTURE_GAP) * d;
  const height = Math.min(PICTURE_MOST * d, room);
  return { picture: { top: PICTURE_TOP * d + (room - height) / 2, height }, name: { top: nameTop }, room };
}

/**
 * A pattern's circle, its name fitted in it; `painted`, its name under its picture, the circle a little larger where its
 * name would leave the picture too little room.
 */
export function circleLook(n: number, name: string, w: PatternWeight, painted = false): CircleLook {
  const hue = patternHue(n);
  const fontSize = FONT[w];
  const tooTight = (d: number, lines: string[]) => (painted ? paintedIn(d, lines, fontSize, RING[w]).room < PICTURE_LEAST * d : lines.length * fontSize * 1.15 > d * 0.62);
  // Grown until the name fits, a few units at a time: never smaller than the step's own size.
  let d = PATTERN_DIAMETERS[w];
  let lines = nameLines(name, fontSize, roomIn(d));
  while (!lines || tooTight(d, lines)) {
    d += 4;
    lines = nameLines(name, fontSize, roomIn(d));
    if (d > 260) break;
  }
  return {
    d,
    fill: w === 5 ? HUE[hue] : `color-mix(in srgb, ${HUE[hue]} ${FILL[w]}%, var(--color-sheet))`,
    edge: w >= 4 ? DEEP[hue] : HUE[hue],
    border: EDGE[w],
    fontSize,
    fontWeight: WEIGHT[w],
    ink: w === 5 ? '#ffffff' : 'var(--color-ink)',
    lines: lines ?? [name],
  };
}

/** Where a painted circle's picture and name sit, from the top inside its ring (`paintedIn`). */
export function paintedPlace(look: Pick<CircleLook, 'd' | 'fontSize' | 'lines'>, w: PatternWeight): { picture: { top: number; height: number }; name: { top: number } } {
  const { picture, name } = paintedIn(look.d, look.lines, look.fontSize, RING[w]);
  return { picture, name };
}

/** A circle faded, not linked to the one selected: its drawing mixed toward the paper, its name in ink, never fainter. */
export function fadedLook(look: CircleLook): Pick<CircleLook, 'fill' | 'edge' | 'ink'> {
  return {
    fill: `color-mix(in srgb, ${look.fill} 42%, var(--color-paper))`,
    edge: `color-mix(in srgb, ${look.edge} 42%, var(--color-paper))`,
    ink: 'var(--color-ink)',
  };
}

/** A pattern's scale's tone, where its picture goes until it is painted (spec §4.7): its hue mixed into the sheet, edged in the hue. */
export function toneLook(n: number): { fill: string; edge: string } {
  const hue = HUE[patternHue(n)];
  return { fill: `color-mix(in srgb, ${hue} 22%, var(--color-sheet))`, edge: hue };
}

/** A small mark of a pattern's step, for a row of the phone: its size, fill and edge as the circle's, scaled down. */
export function markLook(n: number, w: PatternWeight | null): { size: number; fill: string; edge: string; dashed: boolean } {
  const hue = patternHue(n);
  if (w === null) return { size: 14, fill: 'transparent', edge: 'var(--color-rule-strong)', dashed: true };
  return { size: [12, 15, 18, 21, 24][w - 1]!, fill: w === 5 ? HUE[hue] : `color-mix(in srgb, ${HUE[hue]} ${FILL[w]}%, var(--color-sheet))`, edge: w >= 4 ? DEEP[hue] : HUE[hue], dashed: false };
}

/** A painted circle's ring, by step: thicker and deeper the more it matters, as the circle drawn without a picture is. */
const RING = { 1: 1.5, 2: 2, 3: 3, 4: 4, 5: 5 } as const;
/** How much of the hue is behind a painted circle's picture: a wash, never so deep the picture is lost in it. */
const WASH = { 1: 4, 2: 7, 3: 11, 4: 16, 5: 22 } as const;

/**
 * A circle once its picture is painted (Jason, 2026-10-21: "put the icon in the pattern bubble"): the picture sits in its
 * upper part, so how much it matters is its size, its ring, in its scale's hue, thicker and deeper with each step, and a
 * wash of the hue behind the picture, deeper with each step too; its name sits under the picture in ink with no box
 * (Jason, 2026-10-22, of a sheet label on the wash: "the color around the boxes for the titles … looks like trash").
 */
export function ringLook(n: number, w: PatternWeight): { width: number; colour: string; fill: string; ink: string } {
  const hue = patternHue(n);
  const colour = w >= 4 ? DEEP[hue] : w === 3 ? HUE[hue] : `color-mix(in srgb, ${HUE[hue]} ${w === 1 ? 50 : 75}%, var(--color-sheet))`;
  return { width: RING[w], colour, fill: `color-mix(in srgb, ${HUE[hue]} ${WASH[w]}%, var(--color-sheet))`, ink: 'var(--color-ink)' };
}
